import type { QueryRowType } from "@rocicorp/zero";
import { queries, roundedHoursAgo } from "../../shared/queries";
import { config } from "../environment";
import { useGetPresence } from "../hooks/useGetPresence";
import { useIsMobile } from "../hooks/useIsMobile";
import { useQuery } from "../hooks/useQuery";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useUrlSort } from "../hooks/useUrlSort";
import { hasLiveVersion } from "../lib/branch";
import { connectorAvatarItems, mainBranchConnectorIds } from "../lib/connector";
import { makeUrl, routes, useCurrentUrl, useRouteMatch } from "../lib/urls";
import { userDisplayName } from "../lib/userDisplayName";
import {
  type TriggerType,
  triggerTypeOrder,
  workflowTriggerTypes,
} from "../lib/workflowTrigger";
import { ActivityFacepile } from "./ActivityFacepile";
import { Backdrop } from "./Backdrop";
import {
  BuildStatus,
  getBranchListStatus,
  type StatusFilterValue,
  statusFilterOrder,
} from "./BuildStatus";
import { ConnectorAvatars } from "./ConnectorAvatars";
import { DismissibleHeroLayout } from "./DismissibleHeroLayout";
import { EmptyState } from "./EmptyState";
import { TriggerTypes } from "./TriggerTypes";
import {
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
} from "./ListFilter";
import { ListTable } from "./ListTable";
import { countPageCards, PagesList } from "./PagesList";
import { PagesTabHeader } from "./PagesTabHeader";
import { SpaceBadge } from "./SpaceBadge";
import { Text } from "./Text";
import { WorkflowActionsMenu } from "./WorkflowActionsMenu";
import {
  workflowListFilters,
  workflowListSortColumns,
} from "./workflowListFilters";
import { WorkflowNameCell } from "./WorkflowNameCell";

type FavoriteRow = QueryRowType<
  ReturnType<typeof queries.userFavoriteWorkflows>
>;
type FavoriteWorkflow = NonNullable<FavoriteRow["workflow"]>;
type FavoriteSpace = NonNullable<FavoriteWorkflow["space"]>;
type FavoriteCreator = NonNullable<FavoriteWorkflow["createdBy"]>;
type ConnectorInfo = QueryRowType<
  ReturnType<typeof queries.connectedAppsIndex>
>;

function getWorkflowAppIds(
  workflow: Pick<FavoriteWorkflow, "branches">
): string[] {
  return mainBranchConnectorIds(workflow.branches);
}

export function Favorites() {
  const isMobile = useIsMobile();
  const activitySince = roundedHoursAgo(24);
  const [allConnectors] = useQuery(queries.connectedAppsIndex());
  const [favorites, favoritesResult] = useQuery(
    queries.userFavoriteWorkflows({
      activitySince,
    })
  );
  const favoriteApps = favorites;
  const favoriteAppsResult = favoritesResult;
  const [isLinksTab] = useRouteMatch(routes.favorites.links);
  const [workflowViews] = useQuery(queries.userWorkflowViews());
  const connectorById = new Map(allConnectors.map((a) => [a.id, a]));
  const backingAppByWorkflowId = new Map(
    allConnectors
      .filter((a) => a.backingWorkflowId && !a.fresh)
      .map((a) => [a.backingWorkflowId!, a])
  );
  const presenceMap = new Map(
    workflowViews.map((v) => [v.workflowId, v.lastSeenAt])
  );
  const displayName = useSpaceDisplayName();
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.favorites,
    workflowListSortColumns
  );
  const {
    status: statusParam,
    space: spaceParam,
    app: appParam,
    creator: creatorParam,
    trigger: triggerParam,
  } = useCurrentUrl(routes.favorites);
  const statusFilter: StatusFilterValue | null = (
    statusFilterOrder as readonly string[]
  ).includes(statusParam)
    ? (statusParam as StatusFilterValue)
    : null;
  const triggerFilters = parseMultiFilter(triggerParam);
  const spaceFilters = parseMultiFilter(spaceParam);
  const appFilters = parseMultiFilter(appParam);
  const creatorFilters = parseMultiFilter(creatorParam);

  const validFavorites = favorites
    .filter((f) => f.workflow)
    .map((f) => ({
      favorite: f,
      workflow: f.workflow!,
      space: f.workflow!.space!,
    }));

  const getFavoriteBuildStatus = (item: (typeof validFavorites)[number]) => {
    const branch = item.workflow.branches[0];
    if (!branch || branch.name === "main") return null;
    return getBranchListStatus(branch, presenceMap.get(item.workflow.id));
  };

  const availableStatuses = new Set<StatusFilterValue>();
  const availableSpaces = new Map<string, FavoriteSpace>();
  const availableAppIds = new Set<string>();
  const availableCreators = new Map<string, FavoriteCreator>();
  const availableTriggerTypes = new Set<TriggerType>();
  for (const item of validFavorites) {
    const status = getFavoriteBuildStatus(item);
    if (status) availableStatuses.add(status);
    for (const type of workflowTriggerTypes(item.workflow.branches)) {
      availableTriggerTypes.add(type);
    }
    if (item.space) availableSpaces.set(item.space.id, item.space);
    for (const appId of getWorkflowAppIds(item.workflow)) {
      availableAppIds.add(appId);
    }
    if (item.workflow.createdByUserId && item.workflow.createdBy) {
      availableCreators.set(
        item.workflow.createdByUserId,
        item.workflow.createdBy
      );
    }
  }
  const availableStatusOptions = statusFilterOrder.filter((s) =>
    availableStatuses.has(s)
  );
  const availableTriggerOptions = triggerTypeOrder.filter((t) =>
    availableTriggerTypes.has(t)
  );
  const availableSpaceOptions = [...availableSpaces.values()].sort((a, b) =>
    displayName(a).localeCompare(displayName(b))
  );
  const availableApps = [...availableAppIds]
    .map((id) => connectorById.get(id))
    .filter((a): a is ConnectorInfo => a != null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const filteredFavorites = validFavorites.filter((item) => {
    if (statusFilter !== null) {
      if (getFavoriteBuildStatus(item) !== statusFilter) return false;
    }
    if (
      triggerFilters.length > 0 &&
      !workflowTriggerTypes(item.workflow.branches).some((t) =>
        triggerFilters.includes(t)
      )
    )
      return false;
    if (spaceFilters.length > 0) {
      if (!item.space || !spaceFilters.includes(item.space.id)) return false;
    }
    if (appFilters.length > 0) {
      const ids = getWorkflowAppIds(item.workflow);
      if (!appFilters.some((id) => ids.includes(id))) return false;
    }
    if (creatorFilters.length > 0) {
      if (
        !item.workflow.createdByUserId ||
        !creatorFilters.includes(item.workflow.createdByUserId)
      )
        return false;
    }
    return true;
  });

  const filters = workflowListFilters({
    status: { options: availableStatusOptions, value: statusFilter },
    trigger: { options: availableTriggerOptions, values: triggerFilters },
    space: {
      options: availableSpaceOptions,
      values: spaceFilters,
      displayName,
    },
    app: { options: availableApps, values: appFilters },
    creator: { options: availableCreatorOptions, values: creatorFilters },
  });

  const sortedFavorites = [...filteredFavorites].sort((a, b) => {
    if (!sortColumn) {
      const aTime = a.workflow.updatedAt;
      const bTime = b.workflow.updatedAt;
      return bTime - aTime;
    }

    let aVal: string | number;
    let bVal: string | number;

    switch (sortColumn) {
      case "name":
        aVal = a.workflow.name.toLowerCase();
        bVal = b.workflow.name.toLowerCase();
        break;
      case "space":
        aVal = displayName(a.space).toLowerCase();
        bVal = displayName(b.space).toLowerCase();
        break;
      case "createdAt":
        aVal = a.workflow.createdAt;
        bVal = b.workflow.createdAt;
        break;
      case "buildStatus": {
        const getStatusVal = (w: typeof a) => {
          const branch = w.workflow.branches[0];
          if (!branch || branch.name === "main") return 0;
          const status = getBranchListStatus(
            branch,
            presenceMap.get(w.workflow.id)
          );
          const priority: Record<string, number> = {
            waiting: 3,
            ready: 2,
            building: 1,
            thinking: 1,
          };
          return (
            (priority[status ?? ""] ?? 0) * 1e15 + (branch.lastChatEntryAt ?? 0)
          );
        };
        aVal = getStatusVal(a);
        bVal = getStatusVal(b);
        break;
      }
      case "createdBy":
        aVal = (
          a.workflow.createdBy ? userDisplayName(a.workflow.createdBy) : ""
        ).toLowerCase();
        bVal = (
          b.workflow.createdBy ? userDisplayName(b.workflow.createdBy) : ""
        ).toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const displayedFavorites = sortedFavorites;

  const hasBuildStatus = displayedFavorites.some(({ workflow }) => {
    const branch = workflow.branches[0];
    if (!branch || branch.name === "main") return false;
    return getBranchListStatus(branch, presenceMap.get(workflow.id)) != null;
  });

  const { presentByWorkflow: presentUserIdsByWorkflow } = useGetPresence({
    enabled: favoritesResult.type === "complete",
  });

  const pagesRows = favoriteApps
    .map((f) => ({ workflow: f.workflow, space: f.workflow?.space }))
    .filter(
      (
        r
      ): r is {
        workflow: NonNullable<typeof r.workflow>;
        space: NonNullable<typeof r.space>;
      } =>
        r.workflow != null &&
        r.space != null &&
        r.workflow.branches.some((b) => (b.headCommit?.routes?.length ?? 0) > 0)
    );

  return (
    <>
      <title>{`Favorites • ${config.title}`}</title>
      <Backdrop permanentNavigation>
        <DismissibleHeroLayout
          tabHeader={
            <PagesTabHeader
              context={{ kind: "favorites" }}
              workflowsCount={validFavorites.length}
              linksCount={countPageCards(pagesRows)}
            />
          }
        >
          {isLinksTab ? (
            <PagesList
              rows={pagesRows}
              linksRoute={routes.favorites.links}
              showSpaceColumn
              emptyTitle="No favorited links"
              emptyDescription="Favorite a workflow with a link to see it here."
              isComplete={favoriteAppsResult.type === "complete"}
            />
          ) : (
            <ListTable
              columns={{ activity: "fixed-activity" }}
              headers={
                isMobile
                  ? { name: "Workflow" }
                  : {
                      name: "Workflow",
                      buildStatus: hasBuildStatus ? "3B status" : "",
                      space: "Space",
                      apps: "Connectors",
                      trigger: "Trigger",
                      createdAt: "Created",
                      createdBy: "Created by",
                      activity: "",
                      actions: "",
                    }
              }
              sortableColumns={isMobile ? [] : sortableColumns}
              dateColumns={isMobile ? [] : ["createdAt", "buildStatus"]}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              footer={
                hasActiveFilters(filters) ? (
                  <ListFilterPills filters={filters} />
                ) : null
              }
              headerActions={
                validFavorites.length > 0 ? (
                  <ListFilter filters={filters} />
                ) : undefined
              }
              hideHeadersWhenEmpty={validFavorites.length === 0}
              emptyState={
                favoritesResult.type === "complete" ? (
                  validFavorites.length > 0 ? (
                    <EmptyState
                      title="No matches"
                      description="Try a different filter."
                      iconName="heart"
                    />
                  ) : (
                    <EmptyState
                      title="No favorites"
                      description="Workflows you favorite appear here."
                      iconName="heart"
                    />
                  )
                ) : null
              }
              items={displayedFavorites.map(({ workflow, space }) => {
                return {
                  key: workflow.id,
                  link: makeUrl(routes.space.workflow, {
                    spaceId: space?.id ?? "",
                    workflowId: workflow.id,
                  }),
                  name: (hover) => (
                    <WorkflowNameCell
                      workflow={workflow}
                      hover={hover}
                      connectedAppName={
                        backingAppByWorkflowId.get(workflow.id)?.name
                      }
                    />
                  ),
                  buildStatus: (() => {
                    const branch = workflow.branches[0];
                    if (!branch || branch.name === "main") return null;
                    return (
                      <BuildStatus
                        status={getBranchListStatus(
                          branch,
                          presenceMap.get(workflow.id)
                        )}
                      />
                    );
                  })(),
                  space: space ? <SpaceBadge space={space} /> : null,
                  trigger: <TriggerTypes branches={workflow.branches} />,
                  apps: (
                    <ConnectorAvatars
                      connected={connectorAvatarItems(
                        getWorkflowAppIds(workflow),
                        connectorById
                      )}
                    />
                  ),
                  createdAt: (
                    <Text variant="text-small">
                      {new Date(workflow.createdAt).toLocaleDateString()}
                    </Text>
                  ),
                  createdBy: (
                    <Text
                      variant="text-small"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {workflow.createdBy
                        ? userDisplayName(workflow.createdBy)
                        : null}
                    </Text>
                  ),
                  activity: (
                    <ActivityFacepile
                      contributors={workflow.contributors}
                      presentUserIds={presentUserIdsByWorkflow.get(workflow.id)}
                    />
                  ),
                  actions: (hover) => {
                    const workflowSpaceId = space?.id ?? "";
                    return (
                      <div style={{ opacity: hover ? 1 : 0 }}>
                        <WorkflowActionsMenu
                          workflowId={workflow.id}
                          spaceId={workflowSpaceId}
                          hasLiveVersion={hasLiveVersion(workflow.branches)}
                          isFavorite
                          autofixEnabled={workflow.autofixEnabled ?? true}
                          autoImproveEnabled={
                            workflow.autoImproveEnabled ?? false
                          }
                        />
                      </div>
                    );
                  },
                };
              })}
            />
          )}
        </DismissibleHeroLayout>
      </Backdrop>
    </>
  );
}
